/**
 * app.routing.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { ModuleWithProviders }     from "@angular/core";
import { RouterModule, Routes }    from "@angular/router";

import { ForgotPasswordComponent } from "../auth/forgot-password.component";
import { LoginComponent }          from "../auth/login.component";
import { LogoutComponent }         from "../auth/logout.component";
import { Rule }                    from "../auth/permissions/rules";
import { EmptyComponent }          from "../common/empty.component";
import { PageNotFoundComponent }   from "../common/page-not-found.component";
import { WelcomeComponent }        from "../common/welcome.component";
import { WelcomeGuestComponent } 
  from "../common/welcome-guest.component";
import { SettingsComponent }       from "../configuration/settings.component";
import { SandboxComponent }        from "../debug/sandbox.component";
import { IntakeFormComponent }     from "../intake-form/intake-form.component";
import { FirstCheckCustomFlowComponent }
  from "../intake-form/custom-flows/first-check-v1-flow.component";
import { PermalinkComponent }      from "../intake-form/permalink.component";
import { SubjectSummaryComponent }
  from "../intake-form/subject-summary.component";
import { SubjectSearchComponent }  from "../search/subject-search.component";
import { GuardedComponentlessRouteWorkaroundComponent }
  from "../support/patches/guarded-componentless-route-workaround.component";
import { HomeComponent }           from "../watchlist/home.component";

import { AdminZoneGuard }          from "./admin-zone.guard";
import { AlgorithmRunningGuard }   from "./algorithm-running.guard";
import { AuthGuard }               from "./auth.guard";
import { DebugModeGuard }          from "./debug-mode.guard";
import { GuestsOnlyGuard }         from "./guests-only.guard";
import { RuleRestrictedGuard }     from "./rule-restricted.guard";
import { SubjectLoadedGuard }      from "./subject-loaded.guard";
import { WorklistBrowserComponent } from "app/watchlist/lists-browser.component";
import { SharedWorklistComponent } from "app/watchlist/shared-worklist.component";
import { SharedWorklistGuard } from "./shared-worklist.guard";
import { CustomIntakeComponent } from "app/intake-form/intake-custom.component";
import { FeatureTestRigComponent } from "app/debug/feature-test-rig.component";

export const AGGREGATION_MODULE_URL : string = "tools";
export const MANAGEMENT_MODULE_URL : string = "manage";

const appRoutes : Routes = [
  {
    path: "",
    pathMatch: "full",
    component: WelcomeComponent
  },
  {
    path: "guest",
    pathMatch: "full",
    component: WelcomeGuestComponent
  },
  {
    path: "login",
    component: LoginComponent,
    canActivate: [ GuestsOnlyGuard ]
  },
  {
    path: "logout",
    component: LogoutComponent
    // canActivate: [ LogoutGuard ]
  },
  {
    path: "empty",
    component: EmptyComponent
  },
  {
    path: "subject/:id",
    canActivate: [ AuthGuard, SubjectLoadedGuard ],
    data: {post_auth_redirect: "home", existingSessionOnly: true},
    resolve: {subject: SubjectLoadedGuard},
    children: [
      {
        path: "",
        pathMatch: "full",
        component: SubjectSummaryComponent
      },
      {
        path: "custom",
        component: CustomIntakeComponent,
        // canActivate: [ AlgorithmRunningGuard ],
        // canDeactivate: [ AlgorithmRunningGuard ]
      },
      {
        path: ":algorithm",
        component: IntakeFormComponent,
        canActivate: [ AlgorithmRunningGuard ],
        canDeactivate: [ AlgorithmRunningGuard ]
      }
    ]
  },
  {
    path: "lists",
    pathMatch: "full",
    component: WorklistBrowserComponent,
    canActivate: [ AuthGuard ]
  },
  {
    path: "lists/byGroup/:groupId/:objectId",
    canActivate: [AuthGuard, SharedWorklistGuard],
    data: {post_auth_redirect: "lists"},
    resolve: {list: SharedWorklistGuard},
    component: SharedWorklistComponent,
  },
  // {
  //   path: "custom/fcwi/:id",
  //   canActivate: [ AuthGuard, SubjectLoadedGuard, AlgorithmRunningGuard ],
  //   canDeactivate: [ AlgorithmRunningGuard ],
  //   data: {post_auth_redirect: "home", existingSessionOnly: true},
  //   resolve: {subject: SubjectLoadedGuard},
  //   component: FirstCheckCustomFlowComponent
  // },
  {
    path: "x/hcb/:case_id",
    canActivate: [ AuthGuard ],
    component: PermalinkComponent
  },
  {
    path: "home",
    component: HomeComponent,
    canActivate: [ AuthGuard ]
  },
  {
    path: "search",
    component: SubjectSearchComponent,
    canActivate: [ AuthGuard, RuleRestrictedGuard ],
    data: {rules: [Rule.SEARCH_SUBJECT]}
  },
  {
    path: "settings",
    component: SettingsComponent,
    canActivate: [ AuthGuard ]
  },
  {
    path: AGGREGATION_MODULE_URL,
    loadChildren: () => new Promise(function (resolve) {  (require as any).ensure([], function (require: any) {    resolve(require('../aggregation/aggregation.module')['AggregationModule']);  });}),
    canLoad: [ AuthGuard, RuleRestrictedGuard ],
    data: {
    rules: [Rule.RUN_AGGREGATOR, Rule.RUN_DATA_EXPORT],
    post_auth_redirect: AGGREGATION_MODULE_URL
  }
  },
  {
    path: MANAGEMENT_MODULE_URL,
    loadChildren: () => new Promise(function (resolve) {  (require as any).ensure([], function (require: any) {    resolve(require('../management/management.module')['ManagementModule']);  });}),
    canLoad: [ AuthGuard, RuleRestrictedGuard ],
    data: {
    rules: [Rule.VIEW_GROUP_USERS],
    post_auth_redirect: MANAGEMENT_MODULE_URL
  }
  },
  {
    path: "admin",
    loadChildren: () => new Promise(function (resolve) {  (require as any).ensure([], function (require: any) {    resolve(require('../administration/admin-panel.module')['AdminPanelModule']);  });}),
    canLoad: [ AdminZoneGuard, AuthGuard, RuleRestrictedGuard ],
    data: {post_auth_redirect: "admin", rules: [Rule.ADMINISTER_SYSTEM]}
  },
  {
    path: "sandbox",
    component: SandboxComponent,
    canActivate: [ DebugModeGuard ]
  },
  {
    path: "test-rig",
    component: FeatureTestRigComponent,
    canActivate: [ ]
  },
  {
    path: "forgot-password",
    component: ForgotPasswordComponent,
    canActivate: [ GuestsOnlyGuard ]
  },
  {
    // Wildcard for unknown paths. NOTE: No 404 page displayed atm; that will
    // likely be added on the Nginx side.
    path: "**",
    component: PageNotFoundComponent
  }
];

// Guards and other route providers that are useful to the module
export const APPLICATION_GUARDS : any[] = [
  AdminZoneGuard,
  AuthGuard,
  GuestsOnlyGuard,
  AlgorithmRunningGuard,
  SubjectLoadedGuard,
  SharedWorklistGuard,
  DebugModeGuard,
  RuleRestrictedGuard
];

// These are "page" components that need to be listed in the module's
// declarations (this wasn't true in the RCs but will be in the ng2 final
// release). They are included more easily though this array constant.
export const APP_ROUTING_COMPONENTS : any[] = [
  WelcomeComponent,
  WelcomeGuestComponent,
  EmptyComponent,
  FeatureTestRigComponent,
  FirstCheckCustomFlowComponent,
  ForgotPasswordComponent,
  GuardedComponentlessRouteWorkaroundComponent,
  HomeComponent,
  IntakeFormComponent,
  CustomIntakeComponent,
  LoginComponent,
  LogoutComponent,
  PageNotFoundComponent,
  PermalinkComponent,
  SandboxComponent,
  SettingsComponent,
  SharedWorklistComponent,
  SubjectSearchComponent,
  SubjectSummaryComponent,
  WorklistBrowserComponent
];

export const ROOT_ROUTING : ModuleWithProviders =
  RouterModule.forRoot(appRoutes);

// unused now that we switched back to dynamic
// const HARDCODED_ALGORITHM_ROUTES : Routes = [
//   {
//     path: "detailed",
//     component: IntakeFormComponent,
//     data: {algorithm: A_SCREENER},
//     canActivate: [ AlgorithmContextGuard ],
//     canDeactivate: [ AlgorithmContextGuard ]
//   },
//   {
//     path: "simplified",
//     component: IntakeFormComponent,
//     data: {algorithm: A_SIMPLIFIED_SCREENER},
//     canActivate: [ AlgorithmContextGuard ],
//     canDeactivate: [ AlgorithmContextGuard ]
//   },
//   {
//     path: "big-picture",
//     component: IntakeFormComponent,
//     data: {algorithm: A_BIG_PICTURE_SORT},
//     canActivate: [ AlgorithmContextGuard ],
//     canDeactivate: [ AlgorithmContextGuard ]
//   }
// ];



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/routing/app.routing.ts